import { useState } from "react"
import { useQuery } from "@tanstack/react-query"
import { Button } from "../../../components/ui/button"
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "../../../components/ui/tooltip"
import { Separator } from "../../../components/ui/separator"
import { Badge } from "../../../components/ui/badge"
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../../../components/ui/tabs"
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "../../../components/ui/accordion"
import { useBlockchainRequest } from "../../Services/BlockchainRequest"
import { useParams } from "react-router-dom"
import { Check, Copy, ArrowLeft, ExternalLink, FileText, Layers, Loader2, BlocksIcon } from "lucide-react"
import { Card, CardContent, CardHeader, CardTitle } from "../../../components/ui/card";
// import QRCode from "react-qr-code";
import { QRCode } from "react-qrcode-logo";
import { useRef } from "react";
import * as htmlToImage from "html-to-image";
import { QRCodeCanvas } from 'qrcode.react';


// Helper function to format date
const formatDate = (dateString: any) => {
    if (!dateString) return "-";
    const date = new Date(dateString)
    if (isNaN(date.getTime())) return "-";
    return date.toLocaleString("en-US", {
        year: "numeric",
        month: "short",
        day: "numeric",
        hour: "2-digit",
        minute: "2-digit",
        second: "2-digit",
    })
}

const BlockchainDetailsPage = ({ data }) => {
    const qrRef = useRef<HTMLDivElement>(null);
    const [copied, setCopied] = useState(false);
    const { hashid, id } = useParams();
    const { getPublicRequestByHash } = useBlockchainRequest();

    const fetchBlockchainData = async () => {
        const params = new URLSearchParams(window.location.search);
        const type = decodeURIComponent(params.get("type") || "null");
        const code = decodeURIComponent(params.get("code") || "null");

        if (type !== "HASH" || !hashid) {
            throw new Error("Missing query parameters");
        }

        const response: any = await getPublicRequestByHash(hashid, code);
        const convertedArray = JSON.parse(response?.itemDetails);
        const mappedArray = Object.values(convertedArray?.MetaDataFields);

        return {
            ...response,
            blockChainResponse: JSON.parse(response?.blockChainResponse),
            itemDetails: { MetaDataFields: mappedArray }
        };
    };

    const { data: requestData, isLoading } = useQuery({
        queryKey: ['blockchain', hashid],
        queryFn: fetchBlockchainData,
        enabled: !!hashid,
    });

    const copyToClipboard = (text: string) => {
        navigator.clipboard.writeText(text)
        setCopied(true)
        setTimeout(() => setCopied(false), 2000)
    }

    // Extract key information
    const contractAddress =
        requestData?.blockChainResponse?.events.find((e) => e.type === "wasm")?.attributes.find((a) => a.key === "_contract_address")
            ?.value || ""
    const method =
        requestData?.blockChainResponse?.events.find((e) => e.type === "wasm")?.attributes.find((a) => a.key === "method")?.value || ""
    const fee =
        requestData?.blockChainResponse?.events.find((e) => e.type === "fee_pay")?.attributes.find((a) => a.key === "fee")?.value || ""
    const tip =
        requestData?.blockChainResponse?.events.find((e) => e.type === "tip_pay")?.attributes.find((a) => a.key === "tip")?.value || ""
    const sender =
        requestData?.blockChainResponse?.events.find((e) => e.type === "transfer")?.attributes.find((a) => a.key === "sender")?.value || ""
    const recipient =
        requestData?.blockChainResponse?.events.find((e) => e.type === "transfer")?.attributes.find((a) => a.key === "recipient")?.value ||
        ""
    const amount =
        requestData?.blockChainResponse?.events.find((e) => e.type === "transfer")?.attributes.find((a) => a.key === "amount")?.value || ""

    interface BlockchainEvent {
        type: string;
        attributes: { key: string; value: string }[];
    }

    // Group events by type for better organization
    const eventsByType = requestData?.blockChainResponse?.events?.reduce(
        (acc: Record<string, BlockchainEvent[]>, event: BlockchainEvent) => {
            if (!acc[event.type]) {
                acc[event.type] = []
            }
            acc[event.type].push(event)
            return acc
        },
        {} as Record<string, BlockchainEvent[]>
    ) || {};

    const formatKey = (key: string) =>
        key.replace(/([A-Z])/g, ' $1').replace(/^./, (str) => str.toUpperCase());

    const renderValue = (value: any) => {
        if (typeof value === 'boolean') return value ? 'Yes' : 'No';
        if (value === null || value === undefined) return '-';
        return String(value);
    };

    const downloadQR = async () => {
        if (!qrRef.current) return;

        const dataUrl = await htmlToImage.toPng(qrRef.current, {
            cacheBust: true,
            pixelRatio: 3, // sharper image
        });

        const link = document.createElement("a");
        link.download = `${id}-${requestData?.serialNumber}-qr-code.png`;
        link.href = dataUrl;
        link.click();
    };


    return (
        <div className="blockchain-detail">
            {isLoading ? (
                <div>
                    <div className="text-center py-4 mt-16">
                        <Loader2 className="animate-spin h-5 w-5 text-gray-500 mx-auto" />
                    </div>
                </div>
            ) : (
                <div className="">
                    <div className="bg-black p-6 border-b border-gray-800 detail-header">
                        <div className="flex items-center justify-between">
                            <div className="flex items-center space-x-3">
                                <div className="bg-purple-100 p-2 rounded-lg">
                                    <BlocksIcon className="h-6 w-6 text-purple-600" />
                                </div>
                                <div>
                                    <p className="text-xl font-bold text-white">{requestData?.itemName} - {requestData?.itemCatelogName}</p>
                                    <p className="text-sm text-gray-400 mt-1">Invoice: {requestData?.invoiceNumber} {requestData?.blockChainResponse?.height && `| Block Height: ${requestData?.blockChainResponse?.height}`}</p>
                                </div>
                            </div>
                        </div>
                    </div>
                    <div className="bg-black rounded-lg shadow-lg overflow-hidden">
                        {/* <div className="bg-primary text-white p-6">
                            <div className="flex items-center space-x-6 mr-[20px]">
                                <div className="bg-white/20 rounded-full p-3">
                                    <span className="font-bold text-xl">TX</span>
                                </div>
                                <div className="flex-1">
                                    <h1 className="text-2xl font-bold">
                                        {requestData?.itemName} - {requestData?.itemCatelogName}
                                    </h1>
                                    <p className="text-sm opacity-80">
                                        Invoice: {requestData?.invoiceNumber} {requestData?.blockChainResponse?.height && `| Block Height: ${requestData?.blockChainResponse?.height}`}
                                    </p>
                                </div>
                                {requestData?.registrationStatusValue && (<Badge className="bg-green-600 text-white text-sm px-3 py-1 ">{requestData?.registrationStatusValue}</Badge>)}
                            </div>
                        </div> */}
                        <Tabs defaultValue="order" className="w-full">
                            <div className="px-6 pt-6">
                                <TabsList className="grid w-full grid-cols-2 bg-gray-900">
                                    <TabsTrigger value="order" className="flex items-center">
                                        <FileText className="h-4 w-4 mr-2" />
                                        Order Details
                                    </TabsTrigger>
                                    <TabsTrigger value="blockchain" className="flex items-center" disabled={!requestData?.blockChainHash}>
                                        <Layers className="h-4 w-4 mr-2" />
                                        Blockchain Data
                                    </TabsTrigger>
                                </TabsList>
                            </div>
                            <TabsContent value="order" className="p-6">
                                <div className="grid md:grid-cols-2 gap-6">
                                    <Card className="bg-black border-gray-800 text-white">
                                        <CardHeader>
                                            <CardTitle>Order Information</CardTitle>
                                        </CardHeader>
                                        <CardContent className="space-y-4">
                                            <div className="grid grid-cols-2 gap-4">
                                                <div>
                                                    <p className="text-sm text-gray-500">Item Name</p>
                                                    <p className="font-medium">{requestData?.itemName}</p>
                                                </div>
                                                <div>
                                                    <p className="text-sm text-gray-500">Item Code</p>
                                                    <p className="font-medium">{requestData?.itemCode}</p>
                                                </div>
                                                <div>
                                                    <p className="text-sm text-gray-500">Item SKU</p>
                                                    <p className="font-medium">{requestData?.itemSku}</p>
                                                </div>
                                                <div>
                                                    <p className="text-sm text-gray-500">Category</p>
                                                    <p className="font-medium">{requestData?.itemCatelogName}</p>
                                                </div>
                                                <div>
                                                    <p className="text-sm text-gray-500">Description</p>
                                                    <p className="font-medium">{requestData?.itemDescription}</p>
                                                </div>
                                                <div>
                                                    <p className="text-sm text-gray-500">Status</p>
                                                    <Badge variant={requestData?.registrationStatusValue === "Approved" ? "default" : "secondary"}>
                                                        {requestData?.registrationStatusValue}
                                                    </Badge>
                                                </div>
                                            </div>
                                            <Separator />
                                            <div>
                                                <p className="text-sm text-gray-500 mb-2">Item Details</p>
                                                <div className="p-4 rounded-md text-sm font-mono text-gray-300">
                                                    <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-2 gap-4">
                                                        {Object.entries(requestData?.itemDetails ?? {}).map(([attrKey, valueArray]) => {
                                                            if (!Array.isArray(valueArray) || valueArray.length === 0) return null;
                                                            console.log("itemDetails", valueArray);
                                                            if (valueArray[0].masterMetaDataFieldTypeId) {
                                                                return valueArray?.map((item, idx) => (
                                                                    <div key={`${item?.Id}-${idx}`} className="flex flex-col">
                                                                        <span className="text-gray-500">{item?.fieldDisplayName}:</span>
                                                                        <span>{renderValue(item?.fieldValue)}</span>
                                                                    </div>
                                                                ));
                                                            } else {
                                                                return valueArray.map((item, idx) =>
                                                                    Object.entries(item).map(([key, val]) => (
                                                                        <div key={`${key}-${idx}`} className="flex flex-col">
                                                                            <span className="text-gray-500">{formatKey(key)}:</span>
                                                                            <span>{renderValue(val)}</span>
                                                                        </div>
                                                                    ))
                                                                );
                                                            }
                                                        })}
                                                    </div>
                                                </div>
                                            </div>
                                        </CardContent>
                                    </Card>
                                    <Card className="bg-black border-gray-800 text-white">
                                        <CardHeader>
                                            <CardTitle>Request Information</CardTitle>
                                        </CardHeader>
                                        <CardContent className="flex flex-col space-y-4">
                                            <div className="grid grid-cols-2 gap-4">
                                                <div>
                                                    <p className="text-sm text-gray-500">Invoice Number</p>
                                                    <p className="font-medium">{requestData?.invoiceNumber}</p>
                                                </div>
                                                <div>
                                                    <p className="text-sm text-gray-500">Bill Date</p>
                                                    <p className="font-medium">{formatDate(requestData?.billDate)}</p>
                                                </div>
                                                <div>
                                                    <p className="text-sm text-gray-500">Requested By</p>
                                                    <p className="font-medium">{requestData?.requestedBy}</p>
                                                </div>
                                                <div>
                                                    <p className="text-sm text-gray-500">Requested Date</p>
                                                    <p className="font-medium">{formatDate(requestData?.requestedDate)}</p>
                                                </div>
                                                <div>
                                                    <p className="text-sm text-gray-500">Requested From</p>
                                                    <p className="font-medium">{requestData?.requestedFromTenantName}</p>
                                                </div>
                                                <div>
                                                    <p className="text-sm text-gray-500">Requested To</p>
                                                    <p className="font-medium">{requestData?.requestedToTenantName}</p>
                                                </div>
                                                <div>
                                                    <p className="text-sm text-gray-500">Approved By</p>
                                                    <p className="font-medium">{requestData?.approvedBy}</p>
                                                </div>
                                                <div>
                                                    <p className="text-sm text-gray-500">Approved Date</p>
                                                    <p className="font-medium">{formatDate(requestData?.approvedDate)}</p>
                                                </div>
                                            </div>
                                            <br />
                                            <br />
                                            <div ref={qrRef} className="flex flex-col items-center gap-2 bg-white p-4 rounded-lg">
                                                <QRCodeCanvas
                                                    value={`${process.env.REACT_APP_VERIFY_PRODUCT}?serial=${requestData?.serialNumber}&code=${id}`}
                                                    size={250}
                                                    level="H"
                                                    fgColor="#000000"
                                                    bgColor="#ffffff"
                                                    imageSettings={
                                                        {
                                                            src: "/white_black.png",
                                                            height: 100,
                                                            width: 100,
                                                            excavate: true,
                                                        }
                                                    }
                                                />
                                                {/* <QRCode
                                                    value={`${process.env.REACT_APP_PORTAL_URL}blockchain-explorer/${requestData?.serialNumber}?type=HASH&code=${id}`}
                                                    size={250}
                                                    logoImage="/white_black.png"
                                                    logoWidth={60}
                                                    logoHeight={60}
                                                    logoOpacity={1}
                                                    removeQrCodeBehindLogo={true}
                                                    qrStyle="squares"
                                                    eyeRadius={4}
                                                    logoPadding={8}
                                                    quietZone={20}
                                                    fgColor="#000000"
                                                    bgColor="#ffffff"
                                                /> */}

                                                <div className="w-[250px] text-left whitespace-nowrap overflow-visible grid grid-cols-[auto_1fr] gap-x-2">
                                                    <span className="font-bold">Partner Code</span>
                                                    <span>: {id}</span>

                                                    <span className="font-bold">Serial Number</span>
                                                    <span>: {requestData?.serialNumber}</span>
                                                </div>
                                            </div>

                                            <div className="flex justify-end pt-4">
                                                <Button onClick={downloadQR}>
                                                    Download QR
                                                </Button>
                                            </div>

                                        </CardContent>
                                    </Card>
                                </div>
                                <div className="mt-6">
                                    <Card className="bg-black border-gray-800 text-white">
                                        <CardHeader>
                                            <CardTitle>Blockchain Summary</CardTitle>
                                        </CardHeader>
                                        <CardContent>
                                            <div className="grid md:grid-cols-2 gap-x-8 gap-y-4">
                                                <div>
                                                    <p className="text-sm text-gray-500">Transaction Hash</p>
                                                    <div className="flex items-center">
                                                        <p className="font-mono text-sm truncate mr-2">{requestData?.blockChainHash}</p>
                                                        <Button
                                                            variant="ghost"
                                                            size="icon"
                                                            className="h-6 w-6"
                                                            onClick={() => copyToClipboard(requestData?.blockChainHash)}
                                                        >
                                                            <Copy className="h-3 w-3" />
                                                        </Button>
                                                    </div>
                                                </div>
                                                <div>
                                                    <p className="text-sm text-gray-500">Block Height</p>
                                                    <p className="font-medium">{requestData?.blockChainResponse?.height}</p>
                                                </div>
                                                <div>
                                                    <p className="text-sm text-gray-500">Method</p>
                                                    <p className="font-medium">{method}</p>
                                                </div>
                                                <div>
                                                    <p className="text-sm text-gray-500">Contract</p>
                                                    <div className="flex items-center">
                                                        <p className="font-mono text-sm truncate mr-2">{contractAddress.substring(0, 20)}...</p>
                                                        <Button
                                                            variant="ghost"
                                                            size="icon"
                                                            className="h-6 w-6"
                                                            onClick={() => copyToClipboard(contractAddress)}
                                                        >
                                                            <Copy className="h-3 w-3" />
                                                        </Button>
                                                    </div>
                                                </div>
                                            </div>
                                        </CardContent>
                                    </Card>
                                </div>
                            </TabsContent>
                            <TabsContent value="blockchain" className="p-6">
                                <TooltipProvider>
                                    <div className="space-y-6">
                                        <div>
                                            <p className="text-sm text-gray-500 mb-1">Transaction Hash</p>
                                            <div className="flex items-center">
                                                <p className="text-sm font-mono truncate mr-2 bg-gray-900 p-2 rounded flex-1 text-gray-300">
                                                    {requestData?.blockChainResponse?.transactionHash}
                                                </p>
                                                <Tooltip>
                                                    <TooltipTrigger asChild>
                                                        <Button
                                                            variant="outline"
                                                            size="sm"
                                                            className="h-8"
                                                            onClick={() => copyToClipboard(requestData?.blockChainResponse?.transactionHash)}
                                                        >
                                                            {copied ? <Check className="h-4 w-4 mr-1" /> : <Copy className="h-4 w-4 mr-1" />}
                                                            Copy
                                                        </Button>
                                                    </TooltipTrigger>
                                                    <TooltipContent>
                                                        <p>Copy hash</p>
                                                    </TooltipContent>
                                                </Tooltip>
                                            </div>
                                        </div>
                                        <div className="grid md:grid-cols-2 gap-6">
                                            <div>
                                                <p className="text-sm text-gray-500 mb-1">Method</p>
                                                <p className="font-semibold text-lg">{method}</p>
                                            </div>
                                            <div>
                                                <p className="text-sm text-gray-500 mb-1">Contract</p>
                                                <div className="flex items-center">
                                                    <p className="font-mono text-sm truncate mr-2">{contractAddress}</p>
                                                    <Tooltip>
                                                        <TooltipTrigger asChild>
                                                            <Button
                                                                variant="ghost"
                                                                size="icon"
                                                                className="h-6 w-6"
                                                                onClick={() => copyToClipboard(contractAddress)}
                                                            >
                                                                <Copy className="h-3 w-3" />
                                                            </Button>
                                                        </TooltipTrigger>
                                                        <TooltipContent>
                                                            <p>Copy address</p>
                                                        </TooltipContent>
                                                    </Tooltip>
                                                </div>
                                            </div>
                                        </div>
                                        <div className="grid md:grid-cols-2 gap-6">
                                            <div>
                                                <p className="text-sm text-gray-500 mb-1">From</p>
                                                <div className="flex items-center">
                                                    <p className="font-mono text-sm truncate mr-2">{sender}</p>
                                                    <Tooltip>
                                                        <TooltipTrigger asChild>
                                                            <Button
                                                                variant="ghost"
                                                                size="icon"
                                                                className="h-6 w-6"
                                                                onClick={() => copyToClipboard(sender)}
                                                            >
                                                                <Copy className="h-3 w-3" />
                                                            </Button>
                                                        </TooltipTrigger>
                                                        <TooltipContent>
                                                            <p>Copy address</p>
                                                        </TooltipContent>
                                                    </Tooltip>
                                                </div>
                                            </div>
                                            <div>
                                                <p className="text-sm text-gray-500 mb-1">To</p>
                                                <div className="flex items-center">
                                                    <p className="font-mono text-sm truncate mr-2">{recipient}</p>
                                                    <Tooltip>
                                                        <TooltipTrigger asChild>
                                                            <Button
                                                                variant="ghost"
                                                                size="icon"
                                                                className="h-6 w-6"
                                                                onClick={() => copyToClipboard(recipient)}
                                                            >
                                                                <Copy className="h-3 w-3" />
                                                            </Button>
                                                        </TooltipTrigger>
                                                        <TooltipContent>
                                                            <p>Copy address</p>
                                                        </TooltipContent>
                                                    </Tooltip>
                                                </div>
                                            </div>
                                        </div>
                                        <div className="grid md:grid-cols-3 gap-6">
                                            <div>
                                                <p className="text-sm text-gray-500 mb-1">Amount</p>
                                                <p className="font-semibold">{amount}</p>
                                            </div>
                                            <div>
                                                <p className="text-sm text-gray-500 mb-1">Fee</p>
                                                <p className="font-semibold">{fee}</p>
                                            </div>
                                            <div>
                                                <p className="text-sm text-gray-500 mb-1">Tip</p>
                                                <p className="font-semibold">{tip}</p>
                                            </div>
                                        </div>
                                        {requestData?.blockChainResponse?.height && (
                                            <div className="bg-gray-900 p-4 rounded-lg border border-gray-800 flex items-center text-gray-300">
                                                <Check className="h-5 w-5 text-green-500 mr-2" />
                                                <p>Transaction confirmed at block height {requestData?.blockChainResponse?.height}</p>
                                            </div>
                                        )}

                                        <Separator className="my-6" />
                                        <Card className="bg-black border-gray-800 text-white">
                                            <CardHeader>
                                                <CardTitle>Event Details</CardTitle>
                                            </CardHeader>
                                            <CardContent>
                                                <Accordion type="single" collapsible className="w-full">
                                                    {Object.entries(eventsByType ?? {}).map(([eventType, events]: any, index) => (
                                                        <AccordionItem key={index} value={eventType}>
                                                            <AccordionTrigger className="text-left">
                                                                <div className="flex items-center">
                                                                    <Badge className="mr-2 bg-purple-100 text-purple-800 hover:bg-purple-100">
                                                                        {events.length}
                                                                    </Badge>
                                                                    <span>{eventType}</span>
                                                                </div>
                                                            </AccordionTrigger>
                                                            <AccordionContent>
                                                                <div className="space-y-4 pl-2">
                                                                    {events.map((event, eventIndex) => (
                                                                        <div key={eventIndex} className="border border-gray-800 rounded-lg p-4">
                                                                            <h4 className="font-medium mb-2">Event #{eventIndex + 1}</h4>
                                                                            <div className="grid gap-2">
                                                                                {event.attributes.map((attr, attrIndex) => (
                                                                                    <div key={attrIndex} className="grid grid-cols-2">
                                                                                        <span className="text-gray-400">{attr.key}</span>
                                                                                        <div className="flex items-center">
                                                                                            <span className="font-mono text-sm truncate mr-1">{attr.value}</span>
                                                                                            <Button
                                                                                                variant="ghost"
                                                                                                size="icon"
                                                                                                className="h-5 w-5"
                                                                                                onClick={() => copyToClipboard(attr.value)}
                                                                                            >
                                                                                                <Copy className="h-3 w-3" />
                                                                                            </Button>
                                                                                        </div>
                                                                                    </div>
                                                                                ))}
                                                                            </div>
                                                                        </div>
                                                                    ))}
                                                                </div>
                                                            </AccordionContent>
                                                        </AccordionItem>
                                                    ))}
                                                </Accordion>
                                            </CardContent>
                                        </Card>
                                        <Card className="bg-black border-gray-800 text-white">
                                            <CardHeader className="flex flex-row items-center justify-between">
                                                <CardTitle>Blockchain JSON Data</CardTitle>
                                                <Button
                                                    variant="outline"
                                                    size="sm"
                                                    onClick={() => copyToClipboard(JSON.stringify(requestData?.blockChainResponse, null, 2))}
                                                >
                                                    <Copy className="h-4 w-4 mr-1" />
                                                    Copy JSON
                                                </Button>
                                            </CardHeader>
                                            <CardContent >
                                                <pre className="bg-gray-900 p-4 rounded-lg overflow-auto text-sm max-h-[400px] text-gray-300">
                                                    {JSON.stringify(requestData?.blockChainResponse, null, 2)}
                                                </pre>
                                            </CardContent>
                                        </Card>
                                    </div>
                                </TooltipProvider>
                            </TabsContent>
                        </Tabs>
                    </div>
                </div>
            )}
        </div>
    )
}


export default BlockchainDetailsPage;